import {
  CSSResultGroup,
  LitElement,
  PropertyValues,
  TemplateResult,
} from 'lit';
import { html, unsafeStatic } from 'lit/static-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './outline-element.base.css.lit';

import { SiteVariant } from './utils/types';

@customElement('outline-element')
export class OutlineElement extends LitElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * The site context for the element, automagically set.
   */
  @property({ type: String, attribute: 'site', reflect: true })
  site: SiteVariant;

  /**
   * Setting to render only in the specified site.
   */
  @property({ type: String, attribute: 'render-for-site' })
  renderForSite: SiteVariant | null = null;

  @state() shouldRender: boolean;

  /**
   * A manual setting for the site context, primarily used for storybook.
   */
  @property({ type: String, attribute: 'site-override' })
  siteOverride: SiteVariant | null = null;

  /**
   * Same as .closest(), but walking up the DOM crossing shadow root boundaries.
   */
  closestElement(selector: string, el: Element | null = this): Element | null {
    if (!(el instanceof HTMLElement)) {
      return null;
    }
    if (typeof el.closest === 'function') {
      const closestEl = el.closest(selector);
      if (closestEl) {
        return closestEl;
      }
    }

    const rootNode = el.getRootNode();
    if (rootNode instanceof ShadowRoot && rootNode.host) {
      return this.closestElement(selector, rootNode.host);
    }

    return null;
  }

  firstUpdated() {
    // Keeping method to avoid updating all the components that extends this and override firstUpdated().
  }

  shouldUpdate(_changedProperties: PropertyValues): boolean {
    // Just for testing as a quick global check for site prop functionality.
    // document.querySelector('body')?.setAttribute('data-site-variant', 'BH');
    if (!this.site) {
      // Checks to see if a site context is manually set, if not,
      // searches the <body> tag for the site-variant attribute, if it's not set,
      // searches the closest element that have the 'site' attribute, if none found, defaults to GHS
      // and then sets the site prop accordingly.
      const body = document.body;
      this.site = (this.siteOverride ??
        body.getAttribute('data-site-variant') ??
        this.closestElement('[site]')?.getAttribute('site') ??
        'GHS') as SiteVariant;
    }

    this.shouldRender =
      this.renderForSite === null || this.renderForSite === this.site;

    if (!this.shouldRender) {
      // If this element should not be rendered, force it to be hidden in dom.
      this.style.display = 'none';
    }

    return this.shouldRender && super.shouldUpdate(_changedProperties);
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }
  /**
   * Create a conditional slot.
   *
   * Generates the slot if there is slotted content. Includes a wrapper:
   * ```
   * <div id="header">
   *   <slot name="outline-element--header"></slot>
   * </div>
   * ```
   */
  protected _conditionalSlotTemplate({
    elementName,
    slotNameStub,
    wrapperElementType = 'div',
    ariaLabel = null,
  }: {
    elementName: string;
    slotNameStub: string;
    wrapperElementType?: string;
    ariaLabel?: string | null;
  }): TemplateResult | null {
    const namespacedSlotName = `${elementName}--${slotNameStub}`;

    return this.querySelector(`[slot="${namespacedSlotName}"]`) !== null
      ? html`<${unsafeStatic(
          wrapperElementType
        )} id="${slotNameStub}" aria-label="${ifDefined(ariaLabel)}">
          <slot
            name="${namespacedSlotName}"
            @slotchange="${() => this.requestUpdate()}"
          ></slot>
        </${unsafeStatic(wrapperElementType)}>`
      : null;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-element': OutlineElement;
  }
}
